import Cookies from 'js-cookie';
import {
  INQUIRY_COOKIE_EXPIRY_IN_DAYS,
  RECENT_SEARCH_COOKIE,
  SAVED_INQUIRY_KEY,
  SEARCH_INQUIRY_LATEST_KEY,
} from 'src/common/constants';
import useLocalStorage from 'src/common/hooks/useLocalStorage';
import { InquiryData } from 'src/inquiry/types';
import { useRequestInfoContext } from 'src/common/providers/RequestInfoProvider';
import searchPathFromReferer from 'src/listing/utils/searchPathFromReferrer';
import { compress } from 'lz-string';
import { useCallback, useMemo } from 'react';
import { DateString } from '../utils/dateHelpers';
import { sessionStore } from '../utils/storageWrapper';
import { constructRecentSearchCookie } from '../utils/recentInquiryCookieData';

export type TripDuration = `PT${number}H` | 'P1D';

export type SavedInquiryDetails = {
  preferred_date1?: DateString;
  preferred_date2?: DateString;
  preferred_date3?: DateString;
  pickup_time?: string;
  adults?: number;
  children?: number;
  seniors?: number;
  infants?: number;
  captain_required?: boolean;
  trip_length?: TripDuration;
};

type SavedInquiry = {
  details: SavedInquiryDetails;
  saveInquiryDate: string;
  saveInquiryType: 'inquiry';
};

export const INQUIRY_SEARCH_REFERER_KEY = 'inquiry_search_referer';
/** referer when submitting inquiry from listing trip cart, if referrer was search */
export type InquirySearchReferer = string | null;

type UseSavedInquiryReturn = {
  inquiryDetails: SavedInquiryDetails;
  /** @deprecated it is only written, never read (since removing react-univeral inquiry) */
  storeInquiryInSessionStorage: (data: InquiryData) => void;
  inquirySearchReferer: {
    /** Call only from listing page trip cart, where referer _could_ be a `/boat-rental/` route */
    set: () => void;
    /** Call from /inquiry/success/ onClick of either CTA.
     * Else, session storage is naturally cleared on window close */
    clear: () => void;
  };
  storeInquiryInCookies: (data: InquiryData) => void;
};

// TODO: remove
const useSavedInquiry = (): UseSavedInquiryReturn => {
  const [inquiry] = useLocalStorage<SavedInquiry>(
    SAVED_INQUIRY_KEY,
    {} as SavedInquiry,
  );

  const { referrer } = useRequestInfoContext();
  const setInquirySearchReferer = useCallback(() => {
    const searchReferrer = searchPathFromReferer(referrer);
    if (searchReferrer) {
      sessionStore.setItem(
        INQUIRY_SEARCH_REFERER_KEY,
        JSON.stringify(compress(searchReferrer) as InquirySearchReferer),
      );
    }
  }, [referrer]);
  const clearInquirySearchReferer = () => {
    sessionStore.removeItem(INQUIRY_SEARCH_REFERER_KEY);
  };

  const storeInquiryInSessionStorage = (data: InquiryData) => {
    const updatedInquiry = {
      details: data,
      saveInquiryDate: new Date(),
      saveInquiryType: 'inquiry',
    };
    sessionStore.setItem(SAVED_INQUIRY_KEY, JSON.stringify(updatedInquiry));
    sessionStore.setItem(SEARCH_INQUIRY_LATEST_KEY, 'inquiry');
  };

  const storeInquiryInCookies = (data: InquiryData) => {
    const {
      preferred_date1: date,
      trip_length: duration,
      captain_required: captained,
      adults,
    } = data;

    const capacity = adults;

    /**
      * Checking !captained will return false, i.e self-captain,
      * whch might not be intended. If value is undefined, leave it undefined (null).
      */
    const captainedAsString = () => {
      if (captained === true) { return 'true'; }
      if (captained === false) { return 'false'; }
      return null;
    };
    Cookies.set(
      RECENT_SEARCH_COOKIE,
      constructRecentSearchCookie({
        capacity: `${capacity}` || null,
        dates: date ? [date] : [],
        duration: duration || null,
        captained: captainedAsString(),
        max_price: null, // todo, added to satisfy type
        min_price: null, // todo, added to satisfy type
      }),
      { expires: INQUIRY_COOKIE_EXPIRY_IN_DAYS },
    );
  };

  return useMemo(() => ({
    inquiryDetails: inquiry.details,
    storeInquiryInSessionStorage,
    inquirySearchReferer: {
      set: setInquirySearchReferer,
      clear: clearInquirySearchReferer,
    },
    storeInquiryInCookies,
  }), [inquiry.details, setInquirySearchReferer]);
};

export default useSavedInquiry;
